import type { ServiceState } from '@vinz/shared';
import { formatLocal } from './lib/format.js';

/**
 * Console Extend / Release / Retry restore. The API answers 202 once the worker has admitted
 * the request; the work itself happens after, so the outcome is read back from the request's own row.
 * Every retry reuses the request id, which is what makes a retry safe: the worker applies an id once.
 * An outcome that is not final is UNSETTLED, and the caller must send the same id again for the same
 * action - a fresh id on a retry is how an extend whose first attempt did land gets applied twice.
 */

export type ConsoleAction = 'extend' | 'release' | 'retry_restore';
export const EXTEND_HOURS = [4, 8, 24] as const;
export const DEFAULT_EXTEND_HOURS = 8;

export const POLL_EVERY_MS = 2_000;
export const POLL_FOR_MS = 60_000;
/** A 504 means the outcome is unknown, not that it failed - so ask again, a bounded number of times. */
export const ADMIT_ATTEMPTS = 3;

export interface ConsolePermissions { isCloudops: boolean; ownHoldIds: string[] }

export interface ConsoleRow {
  status: 'accepted' | 'applying' | 'done' | 'failed';
  result?: { expiresAt?: string; redeploy?: 'dispatched' | 'failed' };
  message?: string;
}

export interface ConsoleIo {
  post: (path: string, body: unknown) => Promise<{ status: number; body: { message?: string } }>;
  /** Throws on anything but the row; a failed read is retried on the next tick. */
  getRow: (requestId: string) => Promise<ConsoleRow>;
  sleep: (ms: number) => Promise<void>;
}

/** `settled: false` - the request may yet apply, so a retry of the same action must reuse its id. */
export type ConsoleOutcome = { ok: boolean; message: string; settled: boolean };

const STILL_WORKING = "Still working — check back in a minute. Trying again is safe; it won't be applied twice.";

export function doneMessage(action: ConsoleAction, row: ConsoleRow): string {
  if (action === 'extend') {
    return row.result?.expiresAt ? `Extended to ${formatLocal(row.result.expiresAt)}` : 'Extended';
  }
  const started = row.result?.redeploy !== 'failed';
  if (action === 'release') {
    return started ? 'Released — main redeploying' : "Released, but main's redeploy didn't start. Retry restore will show here if it stays on the branch.";
  }
  return started ? 'Restore of main started' : "The redeploy of main didn't start.";
}

export async function runConsoleAction(io: ConsoleIo, requestId: string, action: ConsoleAction, svc: ServiceState,
                                       durationMs?: number): Promise<ConsoleOutcome> {
  const body = { requestId, repo: svc.repo, holdId: svc.holdId, ...(action === 'extend' ? { durationMs } : {}) };

  let res = await io.post(`/console/${action}`, body);
  for (let attempt = 1; res.status === 504 && attempt < ADMIT_ATTEMPTS; attempt++) {
    await io.sleep(POLL_EVERY_MS);
    res = await io.post(`/console/${action}`, body);
  }
  if (res.status === 504) return { ok: false, message: STILL_WORKING, settled: false };
  if (res.status !== 202) {
    return { ok: false, message: res.body.message ?? `That didn't work (HTTP ${res.status}).`, settled: true };
  }

  for (let waited = 0; waited < POLL_FOR_MS; waited += POLL_EVERY_MS) {
    await io.sleep(POLL_EVERY_MS);
    let row: ConsoleRow;
    try {
      row = await io.getRow(requestId);
    } catch (err) {
      // An expired session will not fix itself by polling; the caller refreshes it. Matched by name, since
      // importing api-client here would pull in the page's deploy-time config.
      if ((err as Error)?.name === 'UnauthorizedError') throw err;
      continue;
    }
    if (row.status === 'done') return { ok: true, message: doneMessage(action, row), settled: true };
    if (row.status === 'failed') return { ok: false, message: row.message ?? "That didn't work.", settled: true };
  }
  return { ok: false, message: STILL_WORKING, settled: false };
}

/** Which buttons a row offers. Cosmetic only - the worker re-checks every action - so no answer means none. */
export function allowedActions(svc: ServiceState, perms: ConsolePermissions | undefined) {
  const none = { extend: false, release: false, retry: false, own: false };
  if (!perms || !svc.holdId) return none;
  const own = perms.ownHoldIds.includes(svc.holdId);
  const mayRelease = own || perms.isCloudops;
  // Ran out but never released: CI still skips us-west-2, so a restore of main cannot land there and
  // Retry would only fail again. Release deletes the variables and redeploys main, which is the fix.
  const expiredUnreleased = svc.consistency === 'expired_not_released';
  return {
    own,
    extend: svc.holdState === 'held' && own,
    release: (svc.holdState === 'held' || expiredUnreleased) && mayRelease,
    retry: svc.holdState === 'restore_failed' && !expiredUnreleased && mayRelease,
  };
}
